import { useEffect, useRef, useState } from 'react';
import api from 'api';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { getAttributesWithMergedOptions } from 'shared-helpers/customAttributes';
import { FlexContainer } from 'components/common';
import { DateRangeSelector } from 'components/common/charts';
import { Dropdown, DropdownSelectable } from 'components/common/dropdown';
import { Form, FormGroup, Select, Text } from 'components/common/form';
import { Badge, Button } from 'components/common';
import { FormApi } from 'informed';

interface Props {
  dataFormat: string;
  setDataFormat: React.Dispatch<React.SetStateAction<string>>;
  updateData: React.Dispatch<React.SetStateAction<string>>;
}

const AnalyticsCompetenciesFilters = ({
  dataFormat,
  setDataFormat,
  updateData
}: Props) => {
  const {
    language,
    messages,
    serverConfig: { userCustomAttributes },
    theme,
    user
  } = useReduxState();

  const formApi = useRef<FormApi>(undefined);
  const mounted = useRef<boolean>(undefined);

  const [activeFilters, setActiveFilters] = useState([]);
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [filters, setFilters] = useState([]);
  const [groups, setGroups] = useState([]);
  const [supervisors, setSupervisors] = useState([]);

  useEffect(() => {
    getFiltersData();
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!mounted.current) return;

    const url = getUrl();
    updateData(url);
  }, [fromDate, toDate, filters]);

  const getFiltersData = async () => {
    try {
      const groups = await api.get('/admin/groups-available');
      setGroups(
        groups.data.groups.map((group) => ({
          value: group.id,
          label: group.title
        }))
      );
    } catch (err) {
      setGroups([]);
    }

    try {
      if (user?.roles?.includes('superadmin')) {
        const supervisors = await api.get(
          '/admin/supervisors?numberPerPage=-1'
        );
        setSupervisors(
          supervisors.data.supervisors
            .filter((supervisor) => supervisor.id !== user?.id)
            .map((supervisor) => ({
              value: supervisor.id,
              label: supervisor.email
            }))
        );
      }
    } catch {
      setSupervisors([]);
    }
  };

  const getUrl = () => {
    let apiUrl = '/admin/stats/competencies-and-ambitions';

    const queryParams = [...filters];
    if (fromDate) {
      queryParams.push(`startDate=${fromDate}`);
    }
    if (toDate) {
      queryParams.push(`endDate=${toDate}`);
    }

    queryParams.forEach((param, i) => {
      apiUrl += `${i === 0 ? '?' : '&'}${param}`;
    });

    return apiUrl;
  };

  const handleClearFilter = (clear: { [key: string]: any }) => {
    formApi.current.setValues(clear);
    handleSubmit();
  };

  const handleFormClear = () => {
    formApi.current.reset();
    setFilters([]);
    setActiveFilters([]);
  };

  const handleDateRangeChange = ({ fromDate: from, toDate: to }) => {
    setFromDate(from);
    setToDate(to);
  };

  const viewableUserCustomAttributes = getAttributesWithMergedOptions({
    language,
    onlyViewable: true,
    requestUser: user,
    userCustomAttributes
  });

  const initialValues = {
    groupId: 0,
    supervisorId: 0,
    ...userCustomAttributes.reduce((obj, attr) => {
      obj[attr.key] = '';
      return obj;
    }, {})
  };

  const handleSubmit = () => {
    const { values } = formApi.current.getState();

    const newFilters = [];
    const newActiveFilters = [];
    if (values.groupId) {
      newActiveFilters.push({
        clear: { groupId: 0 },
        label: messages.commonGroup,
        value: groups?.find((group) => group.value === values.groupId)?.label
      });
    }
    if (values.supervisorId) {
      newActiveFilters.push({
        clear: { supervisorId: 0 },
        label:
          values.supervisorId === user?.id
            ? messages.adminSupervisorMyTeamOnly
            : messages.adminSupervisor,
        value:
          values.supervisorId === user?.id
            ? null
            : supervisors?.find(
                (supervisors) => supervisors.value === values.supervisorId
              )?.label
      });
    }

    if (values.groupId) {
      newFilters.push(`groupId=${values.groupId}`);
    }
    delete values.groupId;

    if (values.supervisorId) {
      newFilters.push(`supervisorId=${values.supervisorId}`);
    }
    delete values.supervisorId;

    const attributeFilters = Object.keys(values)
      .filter((key) => values[key] !== '')
      .reduce((obj, key) => {
        obj[key] = values[key];
        return obj;
      }, {});

    Object.keys(attributeFilters).forEach((key) => {
      const attr = viewableUserCustomAttributes.find(
        (attr) => attr.key === key
      );
      newActiveFilters.push({
        clear: { [key]: '' },
        label: attr.title,
        value:
          attr?.type === 'Boolean'
            ? {
                true: messages.commonYes,
                false: messages.commonNo,
                null: messages.commonNotSet
              }[attributeFilters[key]]
            : attributeFilters[key]
      });
    });

    if (attributeFilters) {
      newFilters.push(`customAttributes=${JSON.stringify(attributeFilters)}`);
    }

    setFilters(newFilters);
    setActiveFilters(newActiveFilters);
  };

  return (
    <div className="competencies-filters">
      <FlexContainer style={{ flexWrap: 'wrap' }}>
        <div className="filters">
          <Dropdown
            align="left"
            category="lighter"
            showArrow={true}
            contentStyle={{
              display: 'flex',
              maxHeight: 'none',
              padding: 0
            }}
            styleContentButtons={false}
            title={messages.commonFilters}
          >
            <Form
              allowEmptyStrings={true}
              getApi={(form) => (formApi.current = form)}
              initialValues={{ ...initialValues }}
              onSubmit={handleSubmit}
              style={{ display: 'flex', width: '100%' }}
            >
              {({ formState }) => (
                <div className="inner">
                  <div className="content" data-keep-open={true}>
                    <>
                      <FormGroup label={messages.commonGroups}>
                        <Select
                          field="groupId"
                          isSearchable={true}
                          keepContainerOpen={true}
                          options={[
                            { value: 0, label: messages.commonAll },
                            ...groups
                          ]}
                          usePortal={true}
                        />
                      </FormGroup>

                      <FormGroup label={messages.adminSupervisors}>
                        <Select
                          field="supervisorId"
                          isSearchable={true}
                          keepContainerOpen={true}
                          options={[
                            { value: 0, label: messages.commonAllSupervisors },
                            {
                              value: user?.id,
                              label: messages.adminSupervisorMyTeamOnly
                            },
                            ...supervisors
                          ]}
                          usePortal={true}
                        />
                      </FormGroup>

                      {viewableUserCustomAttributes.map((attr) => {
                        const options =
                          attr.type === 'Boolean'
                            ? [
                                { label: messages.commonAll, value: '' },
                                { label: messages.commonYes, value: true },
                                { label: messages.commonNo, value: false },
                                { label: messages.commonNotSet, value: null }
                              ]
                            : attr.type === 'Number'
                            ? [
                                { label: messages.commonAll, value: '' },
                                ...Array.from(
                                  Array(Math.max(attr.max - attr.min + 1, 1)),
                                  (n, i) => ({
                                    label: i + attr.min,
                                    value: i + attr.min
                                  })
                                )
                              ]
                            : attr.mergedOptions
                            ? [
                                { label: messages.commonAll, value: '' },
                                ...attr.mergedOptions.map((option) => ({
                                  label: option.label,
                                  value: option.key
                                }))
                              ]
                            : [];

                        const additionalSelectProps =
                          attr.type === 'Number'
                            ? { isSearchable: false }
                            : attr.mergedOptions
                            ? { isSearchable: true }
                            : {};

                        return (
                          <FormGroup
                            htmlFor={attr.key}
                            label={attr.title}
                            key={attr.key}
                          >
                            {options.length ? (
                              <Select
                                error={formState.errors[attr.key]}
                                field={attr.key}
                                id={attr.key}
                                keepContainerOpen={true}
                                options={options}
                                usePortal={true}
                                {...additionalSelectProps}
                              />
                            ) : (
                              <Text
                                error={formState.errors[attr.key]}
                                field={attr.key}
                                id={attr.key}
                                type="text"
                              />
                            )}
                          </FormGroup>
                        );
                      })}
                    </>
                  </div>

                  <div className="buttons">
                    <Button
                      category="light"
                      onClick={handleFormClear}
                      style={{ margin: 5 }}
                      text={messages.commonClear}
                    />
                    <Button
                      category="success"
                      disabled={formState.invalid}
                      onClick={handleSubmit}
                      style={{ margin: 5 }}
                      text={messages.commonSearch}
                      type="submit"
                    />
                  </div>
                </div>
              )}
            </Form>
          </Dropdown>
        </div>

        <div className="date">
          <DateRangeSelector
            includeAllTimeOption={true}
            updateData={handleDateRangeChange}
          />
        </div>

        <div className="view">
          <DropdownSelectable
            category="lighter"
            defaultValue="count"
            onChange={(value: 'count' | 'percentage') => setDataFormat(value)}
            options={[
              { text: messages.analyticsGraphAbsoluteUsers, value: 'count' },
              {
                text: messages.analyticsGraphPercentageUsers,
                value: 'percentage'
              }
            ]}
            optionStyle={{ whiteSpace: 'nowrap' }}
            showSelectedText={true}
            value={dataFormat}
          />
        </div>
      </FlexContainer>

      {activeFilters?.length ? (
        <div className="active-filters">
          {activeFilters.map((filter) => (
            <div className="active-filters-item" key={filter.label}>
              <Badge
                isRounded={false}
                onClose={() => handleClearFilter(filter.clear)}
                text={filter.label + (filter.value ? `=${filter.value}` : '')}
              />
            </div>
          ))}
        </div>
      ) : null}

      <style jsx>{`
        .filters {
          margin-right: 20px;
        }
        .date {
          align-items: flex-start;
          display: flex;
          flex-grow: 1;
          gap: 10px;
        }
        .dropdown {
          position: relative;
        }
        .inner {
          display: flex;
          flex-direction: column;
          width: 100%;
        }
        .content {
          padding: 15px 15px;
          max-height: 400px;
          overflow: auto;
        }
        .buttons {
          border-top: 1px solid ${theme.lightGrey};
          padding: 5px 10px 5px;
          text-align: right;
        }
        .active-filters {
          margin-top: 10px;
        }
        .active-filters-item {
          display: inline-block;
          margin: 2px 10px 2px 0;
        }
        .filters :global(.dropdown-content) {
          width: 260px;
        }
        @media (min-width: ${breakpoints.small}px) {
          .filters :global(.dropdown-content) {
            width: 320px;
          }
        }
      `}</style>
    </div>
  );
};

export default AnalyticsCompetenciesFilters;
